import React, { useEffect, useMemo, useReducer, useRef } from 'react';

import { ACTIONS, reducer } from './reducer';
import Popup from '../components/Popup/Popup';
import { filterInTree } from '../../../utils';
import css from './SearchIndustries.module.css';
import BriefPill from '../../BriefPill/BriefPill';
import useClickOutside from '../../../hooks/useClickOutside';
import SearchInput from '../components/SearchInput/SearchInput';
import MultiSelectionTree from '../../MultiSelectionTree/MultiSelectionTree';

const initialState = {
  query: '',
  options: [],
  isPopupOpen: false,
  selectedOptions: [],
  expandedOptions: [],
};

function SearchIndustries({
  options,
  isLoading,
  styles = {},
  applyFilters,
  activeIndustryFilters,
}) {
  const containerRef = useRef();
  const [state, dispatch] = useReducer(reducer, {
    ...initialState,
    options,
  });
  useClickOutside(containerRef, () => dispatch({ type: ACTIONS.CLOSE_POPUP }));

  /** Update tree view when options change */
  useEffect(() => {
    if (options && options.length > 0) {
      dispatch({ type: ACTIONS.UPDATE_OPTIONS, payload: options });
    }
  }, [options]);

  /** Sync active filters with tree view  */
  useEffect(() => {
    dispatch({
      type: ACTIONS.SYNC_SELECTED_OPTIONS,
      payload: activeIndustryFilters,
    });
  }, [activeIndustryFilters]);

  /** Create a flat array from original options tree, it's a faster way to iterate over the tree */
  const flatenOptions = useMemo(() => {
    const sectors = options;
    const subSectors = sectors.flatMap((option) => option.children);
    const industries = subSectors.flatMap((option) => option.children);
    return [sectors, subSectors, industries].flat();
  }, [options]);

  const handleSearchInputClick = () => {
    if (state.isPopupOpen) return;
    dispatch({ type: ACTIONS.OPEN_POPUP, payload: activeIndustryFilters });
  };

  const onCancel = () => {
    dispatch({ type: ACTIONS.CLOSE_POPUP });
  };

  const onApply = () => {
    dispatch({ type: ACTIONS.CLOSE_POPUP });
    applyFilters(
      flatenOptions.filter((option) =>
        state.selectedOptions.includes(option.id),
      ),
    );
  };

  const onClose = (optionId) => {
    const filteredOptions = activeIndustryFilters.filter(
      ({ id }) => id !== optionId,
    );
    applyFilters(filteredOptions);
  };

  const onQueryChange = (query) => {
    dispatch({ type: ACTIONS.UPDATE_QUERY, payload: query });
  };

  const filteredOptions = useMemo(
    () => filterInTree(state.options, state.query),
    [state.options, state.query],
  );

  const clearAll = () => {
    applyFilters([]);

    if (state.isPopupOpen) {
      dispatch({ type: ACTIONS.CLOSE_POPUP });
    }
  };

  return (
    <div
      style={styles}
      ref={containerRef}
      className={css.container}
      data-cy="search-industry"
    >
      <SearchInput
        query={state.query}
        isLoading={isLoading}
        onQueryChange={onQueryChange}
        onClick={handleSearchInputClick}
        clearAll={activeIndustryFilters.length ? clearAll : undefined}
      />
      <div className={css.selected}>
        {activeIndustryFilters.map((option) => (
          <BriefPill
            key={option.id}
            name={option.name}
            className={css.briefPill}
            onClose={() => onClose(option.id)}
          />
        ))}
      </div>
      {state.isPopupOpen && (
        <Popup>
          <MultiSelectionTree
            dispatch={dispatch}
            options={filteredOptions}
            expandAll={Boolean(state.query)}
            selectedOptions={state.selectedOptions}
            expandedOptions={state.expandedOptions}
          />

          <div className={css.footer}>
            <button
              type="button"
              onClick={onCancel}
              className={css.cancelButton}
              data-cy="search-industry-cancel"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onApply}
              className={css.saveButton}
              data-cy="search-industry-apply"
            >
              Apply
            </button>
          </div>
        </Popup>
      )}
    </div>
  );
}

export default SearchIndustries;
